import { Injectable } from '@angular/core';

import { accessToken } from '../shared/state/local-cache';
import { RuntimeConfigService } from './runtime-config';

/**
 * Loads the post-prequal chatbot widget on demand. The loader script
 * (`chat-facade.js`) is fetched from the env-specific CDN; it then lazy-loads
 * the React bundle on first launcher click. The script should be injected at
 * most once per page lifecycle — the facade itself is a no-op on re-injection
 * but there is no reason to duplicate the network request.
 *
 * Env-sourced via `prequalConfig.CHATBOT_WIDGET_URL`, `CHATBOT_API_URL`, and
 * `ADOBE_SCRIPT_URL` (passed to the widget as `adobeAnalyticsScriptSrc`).
 */
@Injectable({ providedIn: 'root' })
export class ChatWidgetService {
  private loaded = false;

  constructor(private runtimeConfig: RuntimeConfigService) {}

  load(opts: { applicationId: string | number }): void {
    if (this.loaded) return;
    const config = this.runtimeConfig.prequalConfig ?? {};
    const widgetUrl: string | undefined = config.CHATBOT_WIDGET_URL;
    const apiUrl: string | undefined = config.CHATBOT_API_URL;

    if (!widgetUrl || !apiUrl) {
      console.warn(
        '[chat-widget] CHATBOT_WIDGET_URL or CHATBOT_API_URL missing from prequalConfig; widget not loaded.'
      );
      return;
    }
    if (!opts.applicationId) {
      console.warn(
        '[chat-widget] applicationId is required; widget not loaded.'
      );
      return;
    }

    const contextToken = accessToken();
    if (!contextToken) {
      console.warn(
        '[chat-widget] access-token (contextToken) is required; widget not loaded.'
      );
      return;
    }

    const adobeScriptUrl = config.ADOBE_SCRIPT_URL as string | undefined;

    (
      window as unknown as {
        chatConfig: {
          apiUrl: string;
          applicationId: string;
          contextToken: string;
          adobeAnalyticsScriptSrc?: string;
        };
      }
    ).chatConfig = {
      apiUrl,
      applicationId: String(opts.applicationId),
      contextToken,
      ...(adobeScriptUrl ? { adobeAnalyticsScriptSrc: adobeScriptUrl } : {}),
    };

    const script = document.createElement('script');
    script.src = `${widgetUrl.replace(/\/$/, '')}/chat-facade.js`;
    script.async = true;
    document.head.appendChild(script);

    this.loaded = true;
  }
}
